/**
 * JourneyDetailPage — trigger/exits, stappen (klikbaar → preview + testmail),
 * funnel, gepland (wie krijgt wanneer wat) en zendhistorie.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { marketingFetch, type MarketingJourney, type MarketingJourneyStep } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader, SectionCard, StatusBadge, STATUS_LABELS, formatDateTime } from './shared';
import { TemplatePreviewDrawer, MarketingSendDrawer } from './drawers';
import { triggerLabel } from './MarketingJourneysPage';

interface JourneyDetail {
  journey: MarketingJourney;
  steps: MarketingJourneyStep[];
  funnel: Record<string, Record<string, number>>;
  sendsPerStep: Record<string, Record<string, number>>;
  eventsPerStep: Record<string, Record<string, number>>;
}

interface ScheduledDay {
  day: string;
  current_step: number;
  n: number;
}

interface ScheduledRow {
  id: string;
  contact_id: string;
  current_step: number;
  next_action_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface SendRow {
  id: string;
  to_email: string;
  subject: string | null;
  status: string;
  sent_at: string | null;
  queued_at: string;
  journey_step_id: string | null;
  suppressed_reason: string | null;
}

const PAGE_SIZE = 25;

const EXIT_LABELS: Record<string, string> = {
  order_placed: 'Nieuwe bestelling geplaatst',
  replied: 'Klant heeft gereageerd',
  unsubscribed: 'Uitgeschreven',
};

function contactLabel(c: Pick<ScheduledRow, 'first_name' | 'last_name' | 'email'>): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return name ? `${name} · ${c.email}` : c.email;
}

function formatDay(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function JourneyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<JourneyDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewStep, setPreviewStep] = useState<MarketingJourneyStep | null>(null);
  const [openSendId, setOpenSendId] = useState<string | null>(null);
  const [scheduledDays, setScheduledDays] = useState<ScheduledDay[] | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [dayRows, setDayRows] = useState<{ rows: ScheduledRow[]; offset: number } | null>(null);
  const [sends, setSends] = useState<{ rows: SendRow[]; total: number; offset: number } | null>(null);
  const [sendFilter, setSendFilter] = useState<{ stepId: string; status: string }>({ stepId: '', status: '' });

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await marketingFetch<JourneyDetail>(`/api/marketing/journeys?id=${id}`);
      setDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const loadScheduledDays = useCallback(async () => {
    if (!id) return;
    try {
      const data = await marketingFetch<{ days: ScheduledDay[] }>(`/api/marketing/journeys?id=${id}&view=scheduled`);
      setScheduledDays(data.days);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Geplande sends laden mislukt');
    }
  }, [id]);

  const loadDayRows = useCallback(
    async (day: string, offset: number) => {
      if (!id) return;
      try {
        const data = await marketingFetch<{ scheduled: ScheduledRow[] }>(
          `/api/marketing/journeys?id=${id}&view=scheduled&day=${day}&limit=${PAGE_SIZE}&offset=${offset}`
        );
        setDayRows({ rows: data.scheduled, offset });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Geplande sends laden mislukt');
      }
    },
    [id]
  );

  const toggleDay = (day: string) => {
    if (openDay === day) {
      setOpenDay(null);
      setDayRows(null);
      return;
    }
    setOpenDay(day);
    setDayRows(null);
    loadDayRows(day, 0);
  };

  const loadSends = useCallback(
    async (offset: number, filter: { stepId: string; status: string }) => {
      if (!id) return;
      try {
        const params = new URLSearchParams({ id, view: 'sends', limit: String(PAGE_SIZE), offset: String(offset) });
        if (filter.stepId) params.set('step_id', filter.stepId);
        if (filter.status) params.set('status', filter.status);
        const data = await marketingFetch<{ sends: SendRow[]; total: number }>(`/api/marketing/journeys?${params}`);
        setSends({ rows: data.sends, total: data.total, offset });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Zendhistorie laden mislukt');
      }
    },
    [id]
  );

  useEffect(() => {
    loadScheduledDays();
  }, [loadScheduledDays]);

  useEffect(() => {
    loadSends(0, sendFilter);
  }, [loadSends, sendFilter]);

  const saveDailyCap = async (raw: string) => {
    if (!id || !detail) return;
    const parsed = parseInt(raw, 10);
    const cap = Number.isNaN(parsed) || parsed < 1 ? undefined : parsed;
    const settings = { ...(detail.journey.settings ?? {}) } as Record<string, unknown>;
    if (cap === undefined) delete settings.enrollment_daily_cap;
    else settings.enrollment_daily_cap = cap;
    setBusy(true);
    setError(null);
    try {
      await marketingFetch(`/api/marketing/journeys?id=${id}`, { method: 'PATCH', body: { settings } });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Instellingen opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: 'active' | 'paused' | 'archived') => {
    if (!id) return;
    if (status === 'active' && !window.confirm('Journey activeren? De engine begint dan met enrollen en versturen.')) return;
    if (status === 'archived' && !window.confirm('Journey archiveren?')) return;
    setBusy(true);
    setError(null);
    try {
      await marketingFetch(`/api/marketing/journeys?id=${id}`, { method: 'PATCH', body: { status } });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status wijzigen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const journey = detail?.journey;

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader
          title={journey?.name ?? 'Journey'}
          subtitle={journey ? `Trigger: ${triggerLabel(journey.trigger)}` : undefined}
          actions={journey && <StatusBadge status={journey.status} />}
        />
        <MarketingNav />

        <div className="p-6 space-y-6">
          <Link to="/marketing/journeys" className="text-sm text-indigo-600 hover:underline">
            ← Alle journeys
          </Link>

          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
          {!journey || !detail ? (
            <p className="text-sm text-gray-400">Laden…</p>
          ) : (
            <>
              <SectionCard title="Status">
                <div className="flex flex-wrap items-center gap-2">
                  {(journey.status === 'draft' || journey.status === 'paused') && (
                    <button
                      onClick={() => setStatus('active')}
                      disabled={busy}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Activeren
                    </button>
                  )}
                  {journey.status === 'active' && (
                    <button
                      onClick={() => setStatus('paused')}
                      disabled={busy}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Pauzeren
                    </button>
                  )}
                  {journey.status !== 'archived' && (
                    <button
                      onClick={() => setStatus('archived')}
                      disabled={busy}
                      className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      Archiveren
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  Bij activering stromen alleen contacten in die het trigger-venster vanaf dat moment bereiken — bestaande
                  klanten vóór het venster stromen niet met terugwerkende kracht in.
                </p>
                <div className="mt-3 text-sm text-gray-600">
                  <span className="text-gray-400">Exit-condities:</span>{' '}
                  {journey.exit_conditions.map((c) => EXIT_LABELS[c] ?? c).join(' · ')}
                </div>
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <label className="text-gray-400" htmlFor="daily-cap">
                    Max. enrollments per dag:
                  </label>
                  <input
                    id="daily-cap"
                    type="number"
                    min={1}
                    defaultValue={(journey.settings?.enrollment_daily_cap as number | undefined) ?? ''}
                    onBlur={(e) => saveDailyCap(e.target.value)}
                    disabled={busy}
                    placeholder="geen"
                    className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-gray-400">leeg = geen limiet (cap beschermt deliverability bij grote doelgroepen)</span>
                </div>
              </SectionCard>

              <SectionCard title="Stappen">
                <ol className="space-y-3">
                  {detail.steps.map((step, i) => {
                    const sends = detail.sendsPerStep[step.id] ?? {};
                    const events = detail.eventsPerStep[step.id] ?? {};
                    const verstuurd = (sends.sent ?? 0) + (sends.delivered ?? 0);
                    const pct = (n: number) => (verstuurd > 0 ? ` (${Math.round((n / verstuurd) * 100)}%)` : '');
                    const waiting = detail.funnel[String(i)]?.active ?? 0;
                    return (
                      <li key={step.id} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                          {step.step_order}
                        </span>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => step.template_id && setPreviewStep(step)}
                            disabled={!step.template_id}
                            className="text-sm font-medium text-gray-900 hover:text-indigo-600 hover:underline text-left disabled:hover:no-underline"
                            title="Bekijk de mail + testmail versturen"
                          >
                            {step.name}
                          </button>
                          <p className="text-xs text-gray-400">
                            {step.wait_days === 0 ? 'Direct' : `Na ${step.wait_days} dag(en)`} · template:{' '}
                            {step.marketing_templates?.name ?? '—'}
                            {step.subject_override ? ` · onderwerp: "${step.subject_override}"` : ''}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {waiting} wachtend · {verstuurd} verstuurd
                            {sends.bounced ? ` · ${sends.bounced} gebounced` : ''}
                            {sends.queued ? ` · ${sends.queued} in wachtrij` : ''}
                          </p>
                          {(events.opened || events.clicked) && (
                            <p className="text-xs text-indigo-600 mt-0.5">
                              {events.opened ? `${events.opened} geopend${pct(events.opened)}` : ''}
                              {events.opened && events.clicked ? ' · ' : ''}
                              {events.clicked ? `${events.clicked} geklikt${pct(events.clicked)}` : ''}
                              {events.replied ? ` · ${events.replied} gereageerd` : ''}
                            </p>
                          )}
                        </div>
                        {step.template_id && (
                          <button
                            onClick={() => setPreviewStep(step)}
                            className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Bekijk mail
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {journey.status === 'active' && (
                  <p className="text-xs text-gray-400 mt-4">Stappen wijzigen kan alleen bij een gepauzeerde of concept-journey.</p>
                )}
              </SectionCard>

              <SectionCard title="Contacten">
                {Object.keys(detail.funnel).length === 0 ? (
                  <p className="text-sm text-gray-400">Nog geen contacten in deze journey.</p>
                ) : (
                  <div className="space-y-2 text-sm">
                    {Object.entries(detail.funnel)
                      .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
                      .map(([step, statuses]) => (
                        <div key={step} className="flex items-center justify-between">
                          <span className="text-gray-600">
                            {parseInt(step, 10) >= detail.steps.length ? 'Na laatste stap' : `Bij stap ${parseInt(step, 10) + 1}`}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {Object.entries(statuses)
                              .map(([status, count]) => `${count}× ${STATUS_LABELS[status] ?? status}`)
                              .join(' · ')}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Gepland — wat gaat er wanneer uit">
                {!scheduledDays || scheduledDays.length === 0 ? (
                  <p className="text-sm text-gray-400">Geen geplande sends.</p>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {Object.entries(
                      scheduledDays.reduce<Record<string, ScheduledDay[]>>((acc, d) => {
                        (acc[d.day] ??= []).push(d);
                        return acc;
                      }, {})
                    ).map(([day, perStep]) => {
                      const total = perStep.reduce((sum, d) => sum + Number(d.n), 0);
                      const dayTotal = perStep
                        .map((d) => {
                          const step = detail.steps[d.current_step];
                          return `${d.n}× ${step ? `${step.step_order}. ${step.name}` : `stap ${d.current_step + 1}`}`;
                        })
                        .join(' · ');
                      const isOpen = openDay === day;
                      return (
                        <div key={day}>
                          <button
                            onClick={() => toggleDay(day)}
                            className="w-full flex items-center justify-between py-2.5 text-sm hover:bg-slate-50 px-1 -mx-1 rounded"
                          >
                            <span className="flex items-center gap-2">
                              <span className="text-gray-400 text-xs w-4">{isOpen ? '▾' : '▸'}</span>
                              <span className="font-medium text-gray-900 capitalize">{formatDay(day)}</span>
                              <span className="text-gray-400 text-xs">{dayTotal}</span>
                            </span>
                            <span className="text-gray-500 text-xs">{total} mails</span>
                          </button>
                          {isOpen && (
                            <div className="pl-7 pb-3">
                              {!dayRows ? (
                                <p className="text-xs text-gray-400">Laden…</p>
                              ) : (
                                <>
                                  <table className="w-full text-sm">
                                    <tbody>
                                      {dayRows.rows.map((row) => {
                                        const step = detail.steps[row.current_step];
                                        return (
                                          <tr key={row.id} className="border-b border-gray-50">
                                            <td className="py-1.5 text-gray-500 whitespace-nowrap text-xs">
                                              {formatDateTime(row.next_action_at)}
                                            </td>
                                            <td className="py-1.5 text-gray-600 truncate max-w-[280px]">{contactLabel(row)}</td>
                                            <td className="py-1.5 text-gray-500 text-xs">
                                              {step ? `${step.step_order}. ${step.name}` : `stap ${row.current_step + 1}`}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  <Pager total={total} offset={dayRows.offset} onPage={(o) => loadDayRows(day, o)} />
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Verstuurd — zendhistorie"
                actions={
                  <div className="flex items-center gap-2">
                    <select
                      value={sendFilter.stepId}
                      onChange={(e) => setSendFilter((f) => ({ ...f, stepId: e.target.value }))}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-600"
                    >
                      <option value="">Alle stappen</option>
                      {detail.steps.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.step_order}. {s.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={sendFilter.status}
                      onChange={(e) => setSendFilter((f) => ({ ...f, status: e.target.value }))}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-600"
                    >
                      <option value="">Alle statussen</option>
                      {['sent', 'delivered', 'queued', 'bounced', 'failed', 'suppressed', 'complained'].map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s] ?? s}
                        </option>
                      ))}
                    </select>
                  </div>
                }
              >
                {!sends || sends.rows.length === 0 ? (
                  <p className="text-sm text-gray-400">Geen sends gevonden.</p>
                ) : (
                  <>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                          <th className="py-2 font-medium">Datum</th>
                          <th className="py-2 font-medium">Ontvanger</th>
                          <th className="py-2 font-medium">Stap</th>
                          <th className="py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sends.rows.map((row) => {
                          const step = detail.steps.find((s) => s.id === row.journey_step_id);
                          return (
                            <tr
                              key={row.id}
                              onClick={() => setOpenSendId(row.id)}
                              className="border-b border-gray-50 cursor-pointer hover:bg-slate-50"
                              title="Bekijk de mail zoals verstuurd"
                            >
                              <td className="py-2 text-gray-700 whitespace-nowrap">{formatDateTime(row.sent_at ?? row.queued_at)}</td>
                              <td className="py-2 text-gray-600 truncate max-w-[280px]">{row.to_email}</td>
                              <td className="py-2 text-gray-500">{step ? `${step.step_order}. ${step.name}` : '—'}</td>
                              <td className="py-2">
                                <StatusBadge status={row.status} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <Pager total={sends.total} offset={sends.offset} onPage={(o) => loadSends(o, sendFilter)} />
                  </>
                )}
              </SectionCard>
            </>
          )}
        </div>
      </div>

      {previewStep?.template_id && (
        <TemplatePreviewDrawer
          templateId={previewStep.template_id}
          title={`Stap ${previewStep.step_order} — ${previewStep.name}`}
          subjectOverride={previewStep.subject_override}
          onClose={() => setPreviewStep(null)}
        />
      )}
      {openSendId && <MarketingSendDrawer sendId={openSendId} onClose={() => setOpenSendId(null)} />}
    </>
  );
}

function Pager({ total, offset, onPage }: { total: number; offset: number; onPage: (offset: number) => void }) {
  if (total <= PAGE_SIZE) return null;
  const from = offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);
  return (
    <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
      <span>
        {from}–{to} van {total}
      </span>
      <div className="flex gap-2">
        <button
          onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
          disabled={offset === 0}
          className="px-2 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
        >
          ← Vorige
        </button>
        <button
          onClick={() => onPage(offset + PAGE_SIZE)}
          disabled={to >= total}
          className="px-2 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
        >
          Volgende →
        </button>
      </div>
    </div>
  );
}
